import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

interface Case {
  id: number;
  case_name: string;
  firm_name: string;
}

interface MedicalRecord {
  id: number;
  name: string;
  created_at: string;
  external_url?: string | null;
  file_path?: string | null;
}

const MedicalRecordsUpload: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [medicalRecords, setMedicalRecords] = useState<MedicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    uploader_name: '',
    uploader_email: '',
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [links, setLinks] = useState<{ label: string; url: string }[]>([{ label: '', url: '' }]);

  useEffect(() => {
    fetchCaseData();
  }, [caseId]);

  const fetchCaseData = async () => {
    if (!caseId) return;

    try {
      const response = await api.get(`/medical-records/${caseId}`);
      setCaseData(response.data.case);
      setMedicalRecords(response.data.medical_records);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load case information');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(files);
      // Auto-fill the names with the file names
      setFileNames(files.map(file => file.name));
    }
  };

  const handleFileNameChange = (index: number, name: string) => {
    const newNames = [...fileNames];
    newNames[index] = name;
    setFileNames(newNames);
  };

  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newNames = fileNames.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setFileNames(newNames);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (selectedFiles.length === 0 && !links.some(link => link.url.trim())) {
      toast.error('Add at least one file or a record link');
      return;
    }

    if (!formData.uploader_name || !formData.uploader_email) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check if all files have names
    if (fileNames.some(name => !name.trim())) {
      toast.error('Please provide names for all files');
      return;
    }

    setUploading(true);

    try {
      const uploadData = new FormData();
      
      // Add files
      selectedFiles.forEach((file, index) => {
        uploadData.append('files[]', file);
        uploadData.append('names[]', fileNames[index]);
      });

      links
        .filter(link => link.url.trim())
        .forEach((link, index) => {
          uploadData.append(`links[${index}][label]`, link.label.trim() || link.url.trim());
          uploadData.append(`links[${index}][url]`, link.url.trim());
        });
      
      // Add user info
      uploadData.append('uploader_name', formData.uploader_name);
      uploadData.append('uploader_email', formData.uploader_email);

      await api.post(`/medical-records/upload/${caseId}`, uploadData);

      const savedCount = selectedFiles.length + links.filter(link => link.url.trim()).length;
      toast.success(`${savedCount} medical record(s) saved`);
      
      // Reset form
      setFormData({
        uploader_name: '',
        uploader_email: '',
      });
      setSelectedFiles([]);
      setFileNames([]);
      setLinks([{ label: '', url: '' }]);
      
      // Refresh the medical records list
      await fetchCaseData();
      
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachmentId: number, fileName: string) => {
    try {
      const response = await api.get(`/medical-records/${caseId}/download/${attachmentId}`, {
        responseType: 'blob',
      });

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = fileName;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob URL and trigger download
      const blob = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started successfully!');
    } catch (err: any) {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Error
          </Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        py: 4,
        px: 2,
      }}
    >
      <Box maxWidth={800} mx="auto">
        {/* Header */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Medical Records Upload"
            subheader={`Case: ${caseData?.case_name}`}
          />
          <CardContent>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              <strong>Firm:</strong> {caseData?.firm_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload medical records and documents for this case. All uploads are secure and confidential.
            </Typography>
          </CardContent>
        </Card>

        {/* Upload Form */}
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Upload Medical Records" />
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Your Name"
                value={formData.uploader_name}
                onChange={(e) => setFormData(prev => ({ ...prev, uploader_name: e.target.value }))}
                margin="normal"
                required
                helperText="Enter your full name"
              />
              
              <TextField
                fullWidth
                label="Your Email"
                type="email"
                value={formData.uploader_email}
                onChange={(e) => setFormData(prev => ({ ...prev, uploader_email: e.target.value }))}
                margin="normal"
                required
                helperText="Enter your email address"
              />

              <Box sx={{ mt: 2, mb: 2 }}>
                <input
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 2 }}
                  >
                    Select Files
                  </Button>
                </label>
                
                {selectedFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Files ({selectedFiles.length}):
                    </Typography>
                    {selectedFiles.map((file, index) => (
                      <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => removeFile(index)}
                          >
                            Remove
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          size="small"
                          label="Document Name"
                          value={fileNames[index]}
                          onChange={(e) => handleFileNameChange(index, e.target.value)}
                          required
                          helperText="Enter a descriptive name for this document"
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Supported formats:</strong> PDF, DOC, DOCX, JPG, PNG<br />
                  <strong>Maximum file size:</strong> 10MB per file<br />
                  Or paste a Google Drive, Dropbox, OneDrive, or other download link instead of a file.
                </Typography>
              </Alert>

              <Typography variant="subtitle2" sx={{ mt: 1 }}>Record links</Typography>
              {links.map((link, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    label="Link label"
                    size="small"
                    value={link.label}
                    onChange={e => {
                      const next = [...links];
                      next[index] = { ...next[index], label: e.target.value };
                      setLinks(next);
                    }}
                  />
                  <TextField
                    label="Download URL"
                    size="small"
                    fullWidth
                    value={link.url}
                    onChange={e => {
                      const next = [...links];
                      next[index] = { ...next[index], url: e.target.value };
                      setLinks(next);
                    }}
                  />
                </Box>
              ))}
              <Button size="small" onClick={() => setLinks(prev => [...prev, { label: '', url: '' }])}>
                Add another link
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={uploading || (selectedFiles.length === 0 && !links.some(link => link.url.trim()))}
                startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                sx={{ mt: 2, display: 'block' }}
              >
                {uploading ? 'Saving...' : 'Submit medical records'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Existing Medical Records */}
        <Card>
          <CardHeader 
            title="Previously Uploaded Medical Records" 
            subheader={`${medicalRecords.length} file(s) uploaded`}
          />
          <CardContent>
            {medicalRecords.length > 0 ? (
              <List>
                {medicalRecords.map((record, index) => (
                  <React.Fragment key={record.id}>
                    <ListItem>
                      <ListItemIcon>
                        <DescriptionIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={record.name}
                        secondary={`Uploaded on ${new Date(record.created_at).toLocaleDateString()}`}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          if (record.external_url) {
                            window.open(record.external_url, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          handleDownload(record.id, record.name);
                        }}
                      >
                        {record.external_url ? 'Open link' : 'Download'}
                      </Button>
                    </ListItem>
                    {index < medicalRecords.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center">
                No medical records have been uploaded yet.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default MedicalRecordsUpload; 